'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var useLocale = require('@narvar/nth-hook-locale');
var nthHookTheme = require('@narvar/nth-hook-theme');
var nthHookTranslations = require('@narvar/nth-hook-translations');
var nthHookSettings = require('@narvar/nth-hook-settings');
var nthHookGlobalSettings = require('@narvar/nth-hook-global-settings');
var useIsMobile = require('../../../common/providers/useIsMobile.js');
var nthHookLoma = require('@narvar/nth-hook-loma');
var SessionContext = require('../../../common/providers/SessionContext.js');
var index = require('../../../common/providers/ClaimBuilder/index.js');
var useAnalytics = require('../../../common/providers/useAnalytics.js');
var PrefetchData = require('./PrefetchData.js');
var useTitanAnalytics = require('../../../common/providers/titan/useTitanAnalytics.js');
var ClaimsGrowthbookProvider = require('../../../common/providers/ClaimsGrowthbookProvider.js');
var useIframeQuery = require('./iframeMessaging/useIframeQuery.js');
var messageSchemas = require('./iframeMessaging/messageSchemas.js');
var ClaimsAgentRootProvider = require('./ClaimsAgentRootProvider.js');
var SupportPlatformCommunicationError = require('./SupportPlatformCommunicationError.js');
var PermissionsProvider = require('./permissions/PermissionsProvider.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var useLocale__default = /*#__PURE__*/_interopDefaultLegacy(useLocale);

const VersionDependentClaimsProviders = _ref => {
  let {
    localeCountry,
    localeLanguage,
    params,
    config,
    children
  } = _ref;
  const {
    supportPlatform,
    retailerMoniker,
    versionName,
    source,
    devMode,
    cache
  } = params;
  const {
    setAuth,
    setParentOrigins
  } = ClaimsAgentRootProvider.useClaimsAgentRoot();
  const {
    supportPlatformOrigins
  } = nthHookSettings.useSettings();
  const requestMessage = {
    type: messageSchemas.MESSAGE_TYPES.READY_FOR_MESSAGES
  };
  const {
    data: initIframeData,
    loading: initIframeLoading,
    error
  } = useIframeQuery.useIframeQuery({
    origins: supportPlatformOrigins,
    requestMessage,
    responseMessageSchema: messageSchemas.initialDataSchema
  });
  const {
    data,
    loading
  } = nthHookLoma.useGetTrackSegmentedExperienceForClaimsQuery({
    variables: {
      retailerMoniker,
      versionName,
      localeCountry,
      localeLanguage
    }
  });
  if (loading || initIframeLoading) return null;
  if (error) {
    config.handleError(error);
    return /*#__PURE__*/React__default["default"].createElement(SupportPlatformCommunicationError["default"], null);
  }
  if (!initIframeData) {
    config.handleError('Insufficient data refetched from support platform');
    return /*#__PURE__*/React__default["default"].createElement(SupportPlatformCommunicationError["default"], null);
  }

  // These update refs, so it's safe to call in render
  setAuth((initIframeData === null || initIframeData === void 0 ? void 0 : initIframeData.payload.token) ?? null);
  setParentOrigins(supportPlatformOrigins ?? null);
  const {
    permissions
  } = initIframeData.payload;
  const permissionsString = Object.keys(permissions).filter(key => permissions[key]).join(',');
  return /*#__PURE__*/React__default["default"].createElement(SessionContext.SessionProvider, {
    value: {
      portalType: 'agent',
      supportPlatform,
      retailerMoniker,
      orderNumber: initIframeData.payload.orderNumber ?? '',
      email: initIframeData.payload.email ?? undefined,
      versionName: (data === null || data === void 0 ? void 0 : data.segmentedExperience.versionName) || '',
      handleError: config.handleError,
      returnsPortalUrlTemplate: config.RETURNS_PORTAL_URL_TEMPLATE,
      localeLanguage,
      localeCountry,
      source,
      devMode,
      cache
    }
  }, /*#__PURE__*/React__default["default"].createElement(index["default"], null, /*#__PURE__*/React__default["default"].createElement(useTitanAnalytics.TitanAnalyticsProvider, {
    value: {
      ...config.titan,
      extra_properties: [{
        key: 'support_platform',
        value: supportPlatform
      }, {
        key: 'permissions',
        value: permissionsString
      }]
    }
  }, /*#__PURE__*/React__default["default"].createElement(ClaimsGrowthbookProvider["default"], {
    growthbookOptions: config.growthbookOptions
  }, /*#__PURE__*/React__default["default"].createElement(PermissionsProvider.PermissionsProvider, {
    value: initIframeData.payload.permissions
  }, children)))));
};
const LocaleDependendentClaimsProviders = _ref2 => {
  let {
    localeCountry,
    localeLanguage,
    params,
    config,
    children
  } = _ref2;
  const {
    retailerMoniker,
    cache
  } = params;
  const isMobile = useIsMobile["default"]();
  const category = isMobile ? 'MOBILE' : 'DESKTOP';
  return /*#__PURE__*/React__default["default"].createElement(nthHookGlobalSettings.LomaGlobalSettingsConfigProvider, {
    retailerMoniker: retailerMoniker,
    localeLanguage: localeLanguage,
    localeCountry: localeCountry,
    cache: cache
  }, /*#__PURE__*/React__default["default"].createElement(nthHookTranslations.LomaTranslationsConfigProvider, {
    product: "claims_translations",
    type: "agent",
    retailerMoniker: "",
    localeLanguage: localeLanguage,
    localeCountry: localeCountry,
    cache: cache
  }, /*#__PURE__*/React__default["default"].createElement(nthHookSettings.LomaSettingsConfigProvider, {
    product: "claims",
    retailerMoniker: retailerMoniker,
    versionName: "",
    localeLanguage: localeLanguage,
    localeCountry: localeCountry,
    category: category,
    type: "agent",
    cache: cache
  }, /*#__PURE__*/React__default["default"].createElement(nthHookTheme.LomaThemeConfigProvider, {
    product: "claims",
    type: "agent",
    retailerMoniker: retailerMoniker,
    versionName: "",
    cache: cache
  }, /*#__PURE__*/React__default["default"].createElement(PrefetchData["default"], null, /*#__PURE__*/React__default["default"].createElement(nthHookSettings.SettingsProvider, {
    settingsRequested: "supportPlatformOrigins"
  }, /*#__PURE__*/React__default["default"].createElement(VersionDependentClaimsProviders, {
    localeLanguage: localeLanguage,
    localeCountry: localeCountry,
    params: params,
    config: config
  }, children)))))));
};
const ClaimsProviders = _ref3 => {
  let {
    params,
    config,
    children
  } = _ref3;
  const {
    locale,
    loading
  } = useLocale__default["default"]();
  const {
    retailerMoniker,
    source
  } = params;
  const {
    handleError,
    NOFLAKE_URL
  } = config;
  if (loading) return null;
  const localeLanguage = (useLocale.isValidLanguage(params.localeLanguage) ? params.localeLanguage : locale.language) ?? 'en';
  const localeCountry = (useLocale.isValidCountry(params.localeCountry) ? params.localeCountry : locale.country) ?? 'US';
  return /*#__PURE__*/React__default["default"].createElement(useAnalytics.AnalyticsProvider, {
    value: {
      retailerMoniker,
      noflakeUrl: NOFLAKE_URL,
      localeLanguage,
      localeCountry,
      source,
      errorCallback: handleError
    }
  }, /*#__PURE__*/React__default["default"].createElement(useLocale.LocaleProvider, {
    value: {
      parameters: {
        country: localeCountry,
        language: localeLanguage,
        retailerMoniker
      },
      handleError
    }
  }, /*#__PURE__*/React__default["default"].createElement(LocaleDependendentClaimsProviders, {
    localeLanguage: localeLanguage,
    localeCountry: localeCountry,
    params: params,
    config: config
  }, children)));
};

exports["default"] = ClaimsProviders;
